import { Injectable } from '@angular/core'

import { StorageService } from '../../../storage.service'
import { IProductTrack } from '../ecommerce-tracking.interfaces'
import { IReservation, IVehicle } from 'app/shared/models/reservation.interfaces'

@Injectable()
export class EcommerceTrackingVehiclesPreparerService {
  constructor(private storageService: StorageService) {}

  public prepareVehiclesForAmendmentTracking(before: IReservation, after: IReservation) {
    const vehicle = this.prepareVehicles(before, after)
    this.storageService.removeSbwAmendmentVehiclesTracking()
    if (vehicle && vehicle.length > 0) {
      this.storageService.setSbwAmendmentVehiclesTracking(JSON.stringify(vehicle))
    }
  }

  public getPreparedVehiclesForAmendmentTracking(before: IReservation, after: IReservation): IProductTrack[] {
    const vehicle = this.prepareVehicles(before, after)
    return vehicle
  }

  private prepareVehicles(before: IReservation, after: IReservation): IProductTrack[] {
    let products: IProductTrack[] = []

    after.departures.forEach((departure, index) => {
      const isOutbound = index === 0
      const departureBefore = before.departures.find((x) => x.departureId === departure.departureId)
      departure.vehicles.forEach((vehicle) => {
        if (!this.vehicleExists(vehicle, departureBefore && departureBefore.vehicles)) {
          let price = this.getVehiclePrice({ reservation: after, vehicle: vehicle, departureId: departure.departureId })
          let name = vehicle.description
          let count = vehicle.count
          let d1 = isOutbound ? 'outbound' : 'return'
          let d5 = 'added'
          let product = this.createVehicleProduct(after, vehicle.id, name, price, count, d1, d5)
          products.push(product)
        }
        if (vehicle.trailer && !this.trailerExists(vehicle, departureBefore && departureBefore.vehicles)) {
          let price = this.getTrailerPrice({ reservation: after, vehicle: vehicle, departureId: departure.departureId })
          let name = vehicle.trailer.description
          let count = vehicle.count
          let d1 = isOutbound ? 'outbound' : 'return'
          let d5 = 'added'
          let product = this.createVehicleProduct(after, vehicle.trailer.id, name, price, count, d1, d5)
          products.push(product)
        }
      })
    })

    before.departures.forEach((departure, index) => {
      const isOutbound = index === 0
      const departureAfter = after.departures.find((x) => x.departureId === departure.departureId)
      departure.vehicles.forEach((vehicle) => {
        if (!this.vehicleExists(vehicle, departureAfter && departureAfter.vehicles)) {
          let price = -this.getVehiclePrice({ reservation: before, vehicle: vehicle, departureId: departure.departureId })
          let name = vehicle.description
          let count = vehicle.count
          let d1 = isOutbound ? 'outbound' : 'return'
          let d5 = 'removed'
          let product = this.createVehicleProduct(before, vehicle.id, name, price, count, d1, d5)
          products.push(product)
        }
        if (vehicle.trailer && !this.trailerExists(vehicle, departureAfter && departureAfter.vehicles)) {
          let price = -this.getTrailerPrice({ reservation: before, vehicle: vehicle, departureId: departure.departureId })
          let name = vehicle.trailer.description
          let count = vehicle.count
          let d1 = isOutbound ? 'outbound' : 'return'
          let d5 = 'removed'
          let product = this.createVehicleProduct(before, vehicle.trailer.id, name, price, count, d1, d5)
          products.push(product)
        }
      })
    })
    return products
  }

  private vehicleExists = (vehicle: IVehicle, vehicles: IVehicle[]): boolean => {
    if (!vehicles || vehicles.length < 1) return false
    return vehicles.some((c) => c.id === vehicle.id && c.count == vehicle.count)
  }

  private trailerExists = (vehicle: IVehicle, vehicles: IVehicle[]): boolean => {
    if (!vehicles || vehicles.length < 1 || !vehicle.trailer) return false    
    return vehicles.some((v) => v.trailer && v.trailer?.id === vehicle.trailer.id)
  }

  private getVehiclePrice({
    reservation,
    vehicle,
    departureId,
  }: {
    reservation: IReservation
    vehicle: IVehicle
    departureId: string
  }): number {
    let price = 0
    let priceItem = reservation.priceItems.find(
      (pi) =>
        pi.departureId != null &&
        pi.departureId.toLowerCase() === departureId.toLowerCase() &&
        pi.label.toLowerCase() === vehicle.description.toLowerCase()
    ) //since id is not the same for vehicle/priceItem, we can only compare label/description
    price += priceItem && priceItem.price
    return price ? price : 0
  }

  private getTrailerPrice({
    reservation,
    vehicle,
    departureId,
  }: {
    reservation: IReservation
    vehicle: IVehicle
    departureId: string
  }): number {
    let price = 0
    let priceItem = reservation.priceItems.find(
      (pi) => pi.departureId != null && pi.departureId.toLowerCase() === departureId.toLowerCase() && pi.id === vehicle?.trailer?.id
    ) //since id is not the same for vehicle/priceItem, we can only compare label/description
    price += priceItem && priceItem.price
    return price ? price : 0
  }

  //This works for both vehicle and trailer
  private createVehicleProduct(
    reservation: IReservation,
    id: string,
    name: string,
    price: number,
    count: number,
    dimension1: string,
    dimension5: string
  ): IProductTrack {
    let product = <IProductTrack>{
      id: id,
      name: name,
      brand: 'SeaBook',
      category: this.getCategory(reservation),
      variant: reservation.productCode[0],
      price: price.toFixed(2),
      quantity: count,
      dimension1: dimension1,
      dimension5: dimension5,
    }
    return product
  }

  private getCategory(reservation: IReservation): string {
    if (!reservation.routeCode) {
      return ''
    }
    if (reservation.routeCode.length !== 4) {
      return reservation.routeCode
    }
    return `${reservation.routeCode}-${reservation.routeCode.substring(2)}${reservation.routeCode.substring(0, 2)}`
  }
}
